type AuditUser = {
  id?: string;
  username?: string;
  email?: string;
  role?: string;
} | null;

type AuditMetadata = Record<string, unknown>;

type AuditEventInput = {
  eventType: string;
  category?: string;
  view?: string;
  path?: string;
  projectId?: string | null;
  resourceId?: string | null;
  requestId?: string | null;
  metadata?: AuditMetadata;
};

type ReplayChunk = {
  session_id: string;
  sequence: number;
  started_at: string;
  ended_at: string;
  events: Record<string, unknown>[];
};

type RrwebModule = {
  record?: (options: Record<string, unknown>) => (() => void) | undefined;
};

type AuditContext = {
  ownerId: string;
  user: NonNullable<AuditUser>;
  sessionId: string;
  pendingEvents: Record<string, unknown>[];
  pendingReplayEvents: Record<string, unknown>[];
  pendingReplayChunks: ReplayChunk[];
  replaySequence: number;
  replayChunkStartedAt: string | null;
  retryFailures: number;
  nextRetryAt: number;
};

const AUDIT_SESSION_KEY = 'pm-audit-session-id-v1';
const AUDIT_DEVICE_KEY = 'pm-audit-device-id-v1';
const AUDIT_BATCH_INTERVAL_MS = 5_000;
const AUDIT_RETRY_BASE_MS = 10_000;
const AUDIT_RETRY_MAX_MS = 300_000;
const AUDIT_MAX_EVENTS_PER_BATCH = 80;
const AUDIT_MAX_PENDING_EVENTS = 500;
const AUDIT_MAX_REPLAY_EVENTS_PER_CHUNK = 120;
const AUDIT_MAX_REPLAY_CHUNKS_PER_BATCH = 25;
const AUDIT_MAX_PENDING_REPLAY_EVENTS = 1000;

let auditApiBaseUrl = '';
let authHeadersProvider: (() => HeadersInit) | null = null;
let authOwnerProvider: (() => string | null) | null = null;
let currentUser: AuditUser = null;
let currentView = '';
const contexts = new Map<string, AuditContext>();
let currentContext: AuditContext | null = null;
let anonymousSessionId: string | null = null;
let inMemoryDeviceId: string | null = null;
let captureGeneration = 0;
let replayStop: (() => void) | null = null;
let flushTimer: number | null = null;
let flushTimerAt = 0;
let flushInFlight: Promise<void> | null = null;
let flushRequested = false;
let forceReplayRequested = false;
let semanticListenersInstalled = false;

function nowIso(): string {
  return new Date().toISOString();
}

function randomId(prefix: string): string {
  return createClientId(prefix);
}

function storageGet(storage: Storage, key: string): string | null {
  try {
    return storage.getItem(key);
  } catch {
    return null;
  }
}

function storageSet(storage: Storage, key: string, value: string): void {
  try {
    storage.setItem(key, value);
  } catch {
    // Storage may be disabled; the in-memory fallback still works for this tab.
  }
}

function windowStorage(name: 'localStorage' | 'sessionStorage'): Storage | null {
  if (typeof window === 'undefined') return null;
  try {
    return window[name];
  } catch {
    return null;
  }
}

export function getAuditSessionId(): string {
  // A stored identifier must never choose the next authenticated owner's
  // session, or reset replay sequence numbers inside a previous page's session.
  if (currentContext) return currentContext.sessionId;
  anonymousSessionId ??= randomId('session');
  return anonymousSessionId;
}

function getAuditDeviceId(): string {
  if (inMemoryDeviceId) return inMemoryDeviceId;
  const storage = windowStorage('localStorage');
  const existing = storage ? storageGet(storage, AUDIT_DEVICE_KEY) : null;
  if (existing) {
    inMemoryDeviceId = existing;
    return inMemoryDeviceId;
  }
  inMemoryDeviceId = randomId('device');
  if (storage) storageSet(storage, AUDIT_DEVICE_KEY, inMemoryDeviceId);
  return inMemoryDeviceId;
}

function getAuditEndpoint(): string {
  const base = auditApiBaseUrl.replace(/\/+$/, '');
  return `${base}/api/v1/audit/events/batch`;
}

function hasAuthorizationHeader(headers: HeadersInit): boolean {
  if (headers instanceof Headers) {
    return Boolean(headers.get('authorization')?.trim());
  }
  if (Array.isArray(headers)) {
    return headers.some(([key, value]) => key.toLowerCase() === 'authorization' && String(value).trim().length > 0);
  }
  return Boolean(
    String(
      (headers as Record<string, unknown>).Authorization ??
      (headers as Record<string, unknown>).authorization ??
      ''
    ).trim()
  );
}

function shouldCaptureReplay(): boolean {
  const raw = String(import.meta.env.VITE_ENABLE_FULL_ACTIVITY_REPLAY ?? 'true').toLowerCase();
  return !['0', 'false', 'off', 'no'].includes(raw);
}

function enqueueEvent(event: Record<string, unknown>): void {
  const context = currentContext;
  if (!context || context.pendingEvents.length >= AUDIT_MAX_PENDING_EVENTS) return;
  context.pendingEvents.push(event);
  scheduleFlush();
  if (context.pendingEvents.length >= AUDIT_MAX_EVENTS_PER_BATCH) {
    void flushAudit();
  }
}

function elementSummary(target: EventTarget | null): Record<string, unknown> {
  if (!(target instanceof Element)) {
    return {};
  }
  const interactive = target.closest('button,a,input,select,textarea,[role="button"],[data-testid]');
  const element = interactive || target;
  return {
    tag: element.tagName.toLowerCase(),
    role: element.getAttribute('role') || undefined,
    ariaLabel: element.getAttribute('aria-label') || undefined,
    title: element.getAttribute('title') || undefined,
    testId: element.getAttribute('data-testid') || undefined,
    text: (element.textContent || '').trim().replace(/\s+/g, ' ').slice(0, 120) || undefined,
  };
}

function installSemanticListeners(): void {
  if (semanticListenersInstalled || typeof document === 'undefined') {
    return;
  }
  semanticListenersInstalled = true;
  document.addEventListener('click', (event) => {
    trackAuditEvent({
      eventType: 'ui.click',
      category: 'ui',
      metadata: elementSummary(event.target),
    });
  }, true);
  document.addEventListener('contextmenu', (event) => {
    trackAuditEvent({
      eventType: 'ui.context_menu',
      category: 'ui',
      metadata: elementSummary(event.target),
    });
  }, true);
  document.addEventListener('submit', (event) => {
    trackAuditEvent({
      eventType: 'ui.form_submit',
      category: 'ui',
      metadata: elementSummary(event.target),
    });
  }, true);
}

function buildReplayChunk(context: AuditContext, force = false): ReplayChunk | null {
  if (context.pendingReplayEvents.length === 0) {
    return null;
  }
  if (!force && context.pendingReplayEvents.length < AUDIT_MAX_REPLAY_EVENTS_PER_CHUNK) {
    return null;
  }
  const events = context.pendingReplayEvents.splice(0, AUDIT_MAX_REPLAY_EVENTS_PER_CHUNK);
  const chunk: ReplayChunk = {
    session_id: context.sessionId,
    sequence: context.replaySequence,
    started_at: context.replayChunkStartedAt || nowIso(),
    ended_at: nowIso(),
    events,
  };
  context.replaySequence += 1;
  context.replayChunkStartedAt = nowIso();
  return chunk;
}

function scheduleFlush(): void {
  if (typeof window === 'undefined') return;
  const context = contexts.get(authOwnerProvider?.() || '');
  if (!context || !(context.pendingEvents.length || context.pendingReplayEvents.length || context.pendingReplayChunks.length)) return;
  const dueAt = Math.max(Date.now() + AUDIT_BATCH_INTERVAL_MS, context.nextRetryAt);
  // A new owner's normal batch must not wait on the previous owner's backoff.
  if (flushTimer !== null) {
    if (flushTimerAt <= dueAt) return;
    window.clearTimeout(flushTimer);
  }
  flushTimerAt = dueAt;
  flushTimer = window.setTimeout(() => {
    flushTimer = null;
    void flushAudit();
  }, dueAt - Date.now());
}

function retryAfterMs(response: Response): number {
  const value = response.headers.get('Retry-After')?.trim() || '';
  const parsed = /^\d+$/.test(value) ? Number(value) * 1000
    : /[a-z]/i.test(value) ? Date.parse(value) - Date.now() : 0;
  return Number.isFinite(parsed) ? Math.max(0, Math.min(parsed, AUDIT_RETRY_MAX_MS)) : 0;
}

export function configureAuditClient(config: { apiBaseUrl: string }): void {
  auditApiBaseUrl = config.apiBaseUrl || '';
}

export function setAuditAuthHeadersProvider(provider: () => HeadersInit, ownerProvider: () => string | null): void {
  authHeadersProvider = provider;
  authOwnerProvider = ownerProvider;
  scheduleFlush();
}

export function setAuditView(view: string): void {
  currentView = view;
}

export function trackAuditEvent(input: AuditEventInput): void {
  if (!currentUser?.id || authOwnerProvider?.() !== currentUser.id) {
    return;
  }
  enqueueEvent({
    event_id: randomId('evt'),
    client_event_id: randomId('client-evt'),
    session_id: getAuditSessionId(),
    event_type: input.eventType,
    category: input.category,
    view: input.view || currentView,
    path: input.path || (typeof window !== 'undefined' ? window.location.pathname + window.location.search : ''),
    project_id: input.projectId || undefined,
    resource_id: input.resourceId || undefined,
    request_id: input.requestId || undefined,
    metadata: input.metadata || {},
    occurred_at: nowIso(),
  });
}

async function startReplay(): Promise<void> {
  const context = currentContext;
  const generation = captureGeneration;
  if (!context || !shouldCaptureReplay() || replayStop || typeof window === 'undefined') {
    return;
  }
  try {
    const rrweb = (await import('./rrwebRecording')) as RrwebModule;
    if (generation !== captureGeneration || currentContext !== context || replayStop || authOwnerProvider?.() !== context.ownerId) return;
    context.replayChunkStartedAt = nowIso();
    replayStop = rrweb.record?.({
      emit(event: Record<string, unknown>) {
        if (generation !== captureGeneration || currentContext !== context || authOwnerProvider?.() !== context.ownerId) return;
        // Preserve the existing bounded offline-retention policy across both
        // queued chunks and events. Failed transport must not grow without limit.
        const retained = context.pendingReplayEvents.length + context.pendingReplayChunks.reduce((count, chunk) => count + chunk.events.length, 0);
        if (retained >= AUDIT_MAX_PENDING_REPLAY_EVENTS) return;
        context.pendingReplayEvents.push(event);
        if (context.pendingReplayEvents.length >= AUDIT_MAX_REPLAY_EVENTS_PER_CHUNK) {
          scheduleFlush();
        }
      },
      maskAllInputs: true,
      maskInputOptions: {
        password: true,
        email: true,
        text: true,
        textarea: true,
        number: true,
        search: true,
        tel: true,
        url: true,
      },
      blockClass: 'audit-block',
      ignoreClass: 'audit-ignore',
      slimDOMOptions: {
        script: true,
        comment: true,
      },
    }) || null;
  } catch {
    if (generation !== captureGeneration || currentContext !== context) return;
    trackAuditEvent({
      eventType: 'audit.replay_unavailable',
      category: 'audit',
      metadata: { reason: 'rrweb_load_failed' },
    });
  }
}

export function startAuditSession(user: AuditUser): void {
  if (!user?.id || authOwnerProvider?.() !== user.id) return;
  if (currentUser?.id && currentUser.id === user?.id) {
    currentUser = { ...user };
    if (currentContext) currentContext.user = currentUser;
    scheduleFlush();
    return;
  }
  stopAuditSession();
  currentUser = { ...user };
  currentContext = contexts.get(user.id) || {
    ownerId: user.id, user: currentUser, sessionId: randomId('session'),
    pendingEvents: [], pendingReplayEvents: [], pendingReplayChunks: [],
    replaySequence: 0, replayChunkStartedAt: null,
    retryFailures: 0, nextRetryAt: 0,
  };
  currentContext.user = currentUser;
  contexts.set(user.id, currentContext);
  captureGeneration += 1;
  const sessionStorage = windowStorage('sessionStorage');
  if (sessionStorage) storageSet(sessionStorage, AUDIT_SESSION_KEY, currentContext.sessionId);
  trackAuditEvent({
    eventType: 'session.start',
    category: 'session',
    metadata: {
      userId: currentUser.id,
      username: currentUser.username,
      role: currentUser.role,
      deviceId: getAuditDeviceId(),
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
    },
  });
  installSemanticListeners();
  void startReplay();
}

export function stopAuditSession(expectedOwnerId?: string): void {
  if (expectedOwnerId && currentUser?.id !== expectedOwnerId) return;
  if (currentUser?.id) {
    trackAuditEvent({ eventType: 'session.stop', category: 'session' });
  }
  if (replayStop) {
    replayStop();
    replayStop = null;
  }
  captureGeneration += 1;
  if (currentContext) {
    while (currentContext.pendingReplayEvents.length > 0) {
      const chunk = buildReplayChunk(currentContext, true);
      if (chunk) currentContext.pendingReplayChunks.push(chunk);
    }
  }
  void flushAudit({ forceReplay: true });
  currentUser = null;
  currentContext = null;
  anonymousSessionId = null;
}

export function createAuditRequestId(): string {
  return randomId('req');
}

export function beginAuditRequest(input: {
  url: string;
  method?: string;
  requestId?: string;
}): string {
  const requestId = input.requestId || createAuditRequestId();
  if (!input.url.includes('/api/v1/audit/events/batch')) {
    trackAuditEvent({
      eventType: 'request.start',
      category: 'request',
      requestId,
      metadata: {
        method: input.method || 'GET',
        url: input.url,
      },
    });
  }
  return requestId;
}

export function endAuditRequest(input: {
  requestId: string;
  url: string;
  method?: string;
  status?: number;
  ok?: boolean;
  error?: unknown;
}): void {
  if (input.url.includes('/api/v1/audit/events/batch')) {
    return;
  }
  trackAuditEvent({
    eventType: input.ok ? 'request.succeeded' : 'request.failed',
    category: 'request',
    requestId: input.requestId,
    metadata: {
      method: input.method || 'GET',
      url: input.url,
      status: input.status,
      error: input.error instanceof Error ? input.error.message : input.error ? String(input.error) : undefined,
    },
  });
}

export function auditRequestHeaders(requestId: string): Record<string, string> {
  return {
    'X-Request-ID': requestId,
    'X-Audit-Session-ID': getAuditSessionId(),
    'X-Audit-Device-ID': getAuditDeviceId(),
  };
}

async function flushAuditPass(forceReplay: boolean): Promise<void> {
  try {
    for (const context of contexts.values()) {
      if (authOwnerProvider?.() !== context.ownerId || Date.now() < context.nextRetryAt) continue;
      const authHeaders = authHeadersProvider?.() || {};
      if (authOwnerProvider?.() !== context.ownerId || !hasAuthorizationHeader(authHeaders)) continue;
      while (context.pendingReplayChunks.length < (forceReplay ? AUDIT_MAX_REPLAY_CHUNKS_PER_BATCH : 1)) {
        const chunk = buildReplayChunk(context, forceReplay);
        if (!chunk) break;
        context.pendingReplayChunks.push(chunk);
      }
      const replayChunks = context.pendingReplayChunks.slice(0, forceReplay ? AUDIT_MAX_REPLAY_CHUNKS_PER_BATCH : 1);
      if (context.pendingEvents.length === 0 && replayChunks.length === 0) continue;
      const events = context.pendingEvents.slice(0, AUDIT_MAX_EVENTS_PER_BATCH);
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 10_000);
      let retryDelay = 0;
      try {
        const headers = new Headers(authHeaders);
        headers.set('Content-Type', 'application/json');
        const body = JSON.stringify({
            session: {
              session_id: context.sessionId, device_id: getAuditDeviceId(),
              metadata: { user_id: context.ownerId, username: context.user.username, role: context.user.role },
            },
            events, replay_chunks: replayChunks,
        });
        const response = await fetch(getAuditEndpoint(), {
          method: 'POST', credentials: 'include', headers, signal: controller.signal,
          keepalive: new TextEncoder().encode(body).byteLength < 60_000,
          body,
        });
        if (!response.ok) {
          retryDelay = retryAfterMs(response);
          // The body also carries the hint when CORS does not expose headers.
          if (response.status === 503) {
            const unavailable = await response.json().catch(() => null);
            if (unavailable?.detail?.code === 'audit_unconfigured') retryDelay = AUDIT_RETRY_MAX_MS;
          }
          throw new Error(`Audit batch was not accepted (${response.status})`);
        }
        const receipt = await response.json();
        if (receipt?.success !== true || receipt?.data?.accepted_session !== true ||
            receipt.data.accepted_events !== events.length || receipt.data.accepted_replay_chunks !== replayChunks.length) {
          throw new Error('Audit batch acknowledgement is incomplete');
        }
        context.retryFailures = 0;
        context.nextRetryAt = 0;
        context.pendingEvents.splice(0, events.length);
        context.pendingReplayChunks.splice(0, replayChunks.length);
      } catch {
        // Includes timeouts, malformed/incomplete ACKs and transient failures.
        // Retain the admitted batch under its original owner until a full ACK.
        context.retryFailures = Math.min(context.retryFailures + 1, 6);
        context.nextRetryAt = Date.now() + Math.min(AUDIT_RETRY_MAX_MS,
          Math.max(retryDelay, AUDIT_RETRY_BASE_MS * 2 ** (context.retryFailures - 1)));
        continue;
      } finally {
        clearTimeout(timeout);
      }
    }
  } finally {
    for (const [ownerId, context] of contexts) {
      const pending = context.pendingEvents.length > 0 || context.pendingReplayEvents.length > 0 || context.pendingReplayChunks.length > 0;
      if (!pending && context !== currentContext) contexts.delete(ownerId);
      if (pending && authOwnerProvider?.() === ownerId) scheduleFlush();
    }
  }
}

export function flushAudit(options?: { forceReplay?: boolean }): Promise<void> {
  if (!authOwnerProvider?.()) return Promise.resolve();
  flushRequested = true;
  forceReplayRequested ||= Boolean(options?.forceReplay);
  if (flushInFlight) return flushInFlight;
  flushInFlight = (async () => {
    do {
      flushRequested = false;
      const forceReplay = forceReplayRequested;
      forceReplayRequested = false;
      await flushAuditPass(forceReplay);
    } while (flushRequested && authOwnerProvider?.());
  })().finally(() => { flushInFlight = null; });
  return flushInFlight;
}

if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    void flushAudit({ forceReplay: true });
  });
}
import { createClientId } from '../utils/randomId';
